import { getCaseTypes } from '../../api/casesApi/caseTypesApi';
import { getClientsList } from '../../api/clientsApi/clientsApi';
import { getDefendantsList } from '../../api/defendantsApi/defendantsApi';
import { getFirmsList } from '../../api/firmsApi/firmsApi';
import { getTrustAccounts } from '../../api/firmsApi/trustAccountsApi';
import { getVendorAccounts } from '../../api/vendorApi/vendorAccountsApi';
import { CaseAttorneyFeesFormValues } from '../../helpers/formSchemas/formDefaultValues';
import { CaseTypeData, PaginationQueryParams } from '../../types';

import { CaseAttorneyFeesConfigProps, CaseAttorneyFeesConfigSubmitProps } from './types';

export const mapPlaintiffsQueryResults = (
  array: { id: string; firstName: string; lastName: string }[],
) => {
  return array.map((item) => ({
    id: item.id,
    label: `${item.firstName} ${item.lastName}`,
  }));
};

export const mapDefendantsQueryResults = (array: { id: string; name: string }[]) => {
  return array.map((item) => ({
    id: item.id,
    label: item.name,
  }));
};

export const handleGetPlaintiffs = async ({
  page,
  size,
  count = true,
  match = '',
  filters,
}: PaginationQueryParams) => {
  return await getClientsList({ page, size, count, match, filters });
};

export const handleGetDefendants = async ({
  page,
  size,
  count = true,
  match = '',
  filters,
}: PaginationQueryParams) => {
  return await getDefendantsList({ page, size, count, match, filters });
};

export const handleGetVendors = async ({
  page,
  size,
  count = true,
  match = '',
  filters,
}: PaginationQueryParams) => {
  return await getVendorAccounts({ page, size, count, match, filters });
};

export const handleGetFirms = async ({
  page,
  size,
  count = true,
  match = '',
  filters,
}: PaginationQueryParams) => {
  return await getFirmsList({ page, size, count, match, filters });
};

export const mapFirmsQueryResults = (
  array: { id: string; name: string }[],
  primaryFirmId?: string,
) => {
  const result = array.map((item) => ({
    id: item.id,
    label: item.name,
  }));

  return primaryFirmId ? result.filter((rItem) => rItem.id !== primaryFirmId) : result;
};

export const mapCounselEntitiesQueryResults = (
  array: { counselEntityType: 'FIRM' | 'VENDOR'; id: number; name: string }[],
  excludeFirmId?: number,
) => {
  return array
    .filter((item) => !(item.counselEntityType === 'FIRM' && item.id === excludeFirmId))
    .map((item) => ({
      id: `${item.counselEntityType}:${item.id}`,
      label: item.name,
      counselEntityType: item.counselEntityType,
    }));
};

export const mapFeesFormValues = (values: CaseAttorneyFeesConfigSubmitProps) => {
  const firmsAttyFeesConfigs = values?.secondaryFirms?.length
    ? values?.secondaryFirms.map((firm) => ({
      fixedAmount: firm.config?.fixedFeeAmount
        ? Number(firm.config?.fixedFeeAmount.replace(/,/g, ''))
        : null,
      variableFeePercent: firm.config?.variableFeePercent
        ? Number(firm.config?.variableFeePercent.replace(/,/g, ''))
        : null,
      maximumAmount: firm?.config?.variableFeeMaximumAmount
        ? Number(firm.config?.variableFeeMaximumAmount.replace(/,/g, ''))
        : null,
      firmId: firm?.id,
    }))
    : [];

  const submitData = {
    case: {
      variableFeePercent: values.caseFeesConfig?.variableFeePercent
        ? Number(values.caseFeesConfig?.variableFeePercent.replace(/,/g, ''))
        : null,
      maximumAmount: values.caseFeesConfig?.maximumAmount
        ? Number(values.caseFeesConfig?.maximumAmount.replace(/,/g, ''))
        : null,
    },
    secondaryFirms: firmsAttyFeesConfigs,
  };

  return submitData;
};

export const mapAttorneyFormValues = (
  values: CaseAttorneyFeesFormValues,
): CaseAttorneyFeesConfigProps => {
  const parseValue = (value?: string | null): number | null => {
    if (!value?.trim()) return null;

    const parsedValue = Number(value.replace(/,/g, ''));

    return Number.isNaN(parsedValue) ? null : parsedValue;
  };

  return {
    variableFeePercent: parseValue(
      values.caseFeesConfig.variableFeePercent,
    ),
    maximumAmount: parseValue(
      values.caseFeesConfig.maximumAmount,
    ),
    secondaryFirms: values.secondaryFirms.map((firm) => ({
      firmId: firm.id,
      role: firm.role,
      fixedFeeAmount: parseValue(firm.config.fixedFeeAmount),
      variableFeePercent: parseValue(
        firm.config.variableFeePercent,
      ),
      variableFeeMaximumAmount: parseValue(
        firm.config.variableFeeMaximumAmount,
      ),
    })),
  };
};

export const handleGetCaseTypes = async ({
  page,
  size,
  count = true,
  match = '',
  filters,
  includeGlobal = true,
}: PaginationQueryParams) => {
  return await getCaseTypes({ page, size, count, match, filters, includeGlobal });
};

export const mapCaseTypesQueryResults = (array: CaseTypeData[]) => {
  return array.map((item) => ({
    id: String(item.id),
    label: item.name,
  }));
};

export const mapVendorsQueryResults = (
  array: {
    id: string;
    name: string;
    defaultVendorPaymentMethod?: string;
    activePaymentMethod?: string[];
    achAccounts?: {
      id: number | string;
      nickname: string;
      accountType: string;
      routingNumber?: string;
      maskedAccountNumber?: string;
      accountNumberLast4?: string;
      isDefault: boolean;
    }[];
    checkAccounts?: {
      id: number | string;
      nickname: string;
      payeeName?: string;
      recipientName?: string;
      address: string;
      addressLine2?: string;
      city: string;
      state: string;
      zip: string;
      country?: string;
      isDefault: boolean;
      mailingPhoneNumber?: string;
    }[];
    accounting?: { isSynced?: boolean; externalVendorId?: string | null };
  }[],
) => {
  return array.map((item) => ({
    id: item.id,
    label: item.name,
    defaultPaymentMethod: item.defaultVendorPaymentMethod,
    activePaymentMethod: item.activePaymentMethod,
    achAccounts: item.achAccounts,
    checkAccounts: item.checkAccounts?.map((account) => ({
      ...account,
      mailingPhoneNumber: account.mailingPhoneNumber || '',
    })),
    accounting: item.accounting,
  }));
};

export const handleGetTrustAccounts = async ({
  page,
  size,
  count = true,
  match = '',
  filters,
  activeSortField,
}: PaginationQueryParams) => {
  return await getTrustAccounts({ page, size, count, match, filters, activeSortField });
};

export const mapTrustAccountsQueryResults = (array: { id: string; name: string }[]) => {
  return array.map((item) => ({
    id: item.id,
    label: item.name,
  }));
};
